import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { useServerSideFacetExpansion } from '@/pages/Search/SearchPage/hooks/useServerSideFacetExpansion';
import { CurrentOpenMenu, STANDARD_VIEW_MORE_SIZE } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getMergedOptionFacet, getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import {
    filterFlatFacetOptionsOwnedByPrecedence,
    isFacetOptionCheckedForSemanticUrl,
    isFlatFacetOptionCheckedForCategoryIdUrl,
} from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

import { FacetCard } from '../FacetCard/FacetCard';

export const CreatorFilter = () => {
    const { formatMessage } = useIntl();
    const { isLoading, onViewMoreClick } = useServerSideFacetExpansion({
        facetId: FacetTypes.CREATOR_ID,
        size: STANDARD_VIEW_MORE_SIZE,
    });
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const creatorFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.CREATOR_ID));
    const rawFacets = useAppSelector(getSearchFacets);
    const { options: mergedCreatorOptions = [] } = creatorFacet;
    const options = useMemo(
        () =>
            filterFlatFacetOptionsOwnedByPrecedence<FacetOption>(
                mergedCreatorOptions,
                rawFacets,
                FacetTypes.CREATOR_ID
            ),
        [mergedCreatorOptions, rawFacets]
    );

    const isOptionChecked = useCallback(
        (option: FacetOption) => {
            const checkedFromLegacyCategoryId = isFlatFacetOptionCheckedForCategoryIdUrl(
                queryParams as Record<string, unknown>,
                option.id,
                FacetTypes.CREATOR_ID,
                rawFacets,
                option.selected
            );
            return isFacetOptionCheckedForSemanticUrl(
                queryParams as Record<string, unknown>,
                FacetTypes.CREATOR_ID,
                option,
                checkedFromLegacyCategoryId
            );
        },
        [queryParams, rawFacets]
    );

    const onCreatorOptionClick = (option: FacetOption) => {
        const newCheckStatus = !isOptionChecked(option);
        const semanticValue = urlSafeTitle(option.name ?? String(option.id));

        let newParams = modifyParamsWhenChecked(queryParams, FacetTypes.CREATOR_ID, semanticValue, newCheckStatus, {
            useSemanticAttributeParams: true,
        });

        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            browseFacetOptionRemovedId: newCheckStatus ? undefined : option.id,
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: newCheckStatus ? FilterAction.CreatorChanged : FilterAction.CreatorRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const filteredOptions = useMemo(
        () =>
            options
                .filter((option) => option.count !== 0 || isOptionChecked(option))
                .sort((firstOption, secondOption) => secondOption.count - firstOption.count),
        [options, isOptionChecked]
    );

    if (filteredOptions.length === 0) {
        return null;
    }

    return (
        <FacetCard
            headline={formatMessage({ id: 'search_facet_title_creator' })}
            isLoading={isLoading}
            onViewMoreClick={onViewMoreClick}
        >
            {filteredOptions.map((option) => (
                <Checkbox.Option
                    checked={isOptionChecked(option)}
                    disabled={option.count === 0 && !isOptionChecked(option)}
                    key={option.id}
                    onChange={() => onCreatorOptionClick(option)}
                >
                    {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                </Checkbox.Option>
            ))}
        </FacetCard>
    );
};
